import { useEffect, useState } from "react";
import { useWorkspaces } from "../state";
import { projectName } from "../workspaces/names";
import { checkedFileIds, filesByCategory, isChecked, PreviewFile } from "./startProject";

// Start project: naam, welke percelen en welke documenten. Wat bij de gekozen
// percelen hoort (hun onderzoek, eigendoms-PDF) staat vooraf aan; dat beslist de
// server (`preselected_file_ids`). Een andere perceelkeuze haalt een nieuwe
// voorselectie op, zonder wat de gebruiker zelf aanvinkte te overschrijven.
export const StartProjectDialog = ({
  exploration,
  onClose,
  onStarted,
}: {
  exploration: any;
  onClose: () => void;
  onStarted: (projectId: number) => void;
}) => {
  const startProject = useWorkspaces((s) => s.startProject);
  const parcels: any[] = exploration.parcels || [];
  const [parcelIds, setParcelIds] = useState<number[]>(parcels.map((p) => p.id));
  // De voorgestelde naam volgt de gekozen percelen, tot de gebruiker zelf typt.
  const [typedName, setTypedName] = useState<string>();
  const name =
    typedName ??
    projectName(
      parcels.filter((p) => parcelIds.includes(p.id)),
      exploration.name
    );
  const [preview, setPreview] = useState<PreviewFile[]>();
  const [manual, setManual] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const parcelKey = parcelIds.join(",");
  useEffect(() => {
    const query = new URLSearchParams();
    for (const id of parcelKey ? parcelKey.split(",") : []) query.append("parcels", id);
    // Een snelle klikreeks op de percelen mag geen oude voorselectie laten winnen.
    const controller = new AbortController();
    fetch(`/api/workspaces/${exploration.id}/start-project/preview?${query}`, {
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((files: PreviewFile[]) => setPreview(files))
      .catch(() => {
        if (!controller.signal.aborted) setError("Kon de documenten niet ophalen");
      });
    return () => controller.abort();
  }, [exploration.id, parcelKey]);

  const toggleParcel = (id: number) =>
    setParcelIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const result = await startProject(exploration.id, {
      name: name.trim(),
      parcelIds,
      fileIds: checkedFileIds(preview ?? [], manual),
    });
    setBusy(false);
    if (result.id) onStarted(result.id);
    else setError(result.error);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={submit}
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
      >
        <h2 className="text-lg font-bold">Start project</h2>
        <p className="mt-1 text-xs text-gray-600">
          Het project krijgt een kopie van de gekozen percelen, met hun tags en eigendomsinformatie,
          en van de aangevinkte documenten. De verkenning blijft gewoon staan.
        </p>

        <label className="mt-4 block text-xs font-semibold" htmlFor="project-name">
          Naam
        </label>
        <input
          id="project-name"
          value={name}
          onChange={(e) => setTypedName(e.target.value)}
          className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
        />

        <fieldset className="mt-4 space-y-1 text-xs">
          <legend className="font-semibold">Percelen</legend>
          {parcels.map((parcel) => (
            <label key={parcel.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={parcelIds.includes(parcel.id)}
                onChange={() => toggleParcel(parcel.id)}
              />
              {parcel.name}
            </label>
          ))}
        </fieldset>

        <fieldset className="mt-4 space-y-2 text-xs">
          <legend className="font-semibold">Documenten</legend>
          {!preview ? (
            <p className="text-gray-500">Documenten ophalen…</p>
          ) : preview.length === 0 ? (
            <p className="text-gray-500">Deze verkenning heeft geen documenten.</p>
          ) : (
            filesByCategory(preview).map(([category, files]) => (
              <div key={category}>
                <p className="text-gray-500">{category || "Losse bestanden"}</p>
                {files.map((file) => (
                  <label key={file.id} className="flex items-center gap-2 pl-2">
                    <input
                      type="checkbox"
                      checked={isChecked(file, manual)}
                      onChange={(e) => setManual((m) => ({ ...m, [file.id]: e.target.checked }))}
                    />
                    <span className="truncate">{file.name}</span>
                  </label>
                ))}
              </div>
            ))
          )}
          <p className="text-gray-500">
            Aangevinkt is wat bij de gekozen percelen hoort: hun onderzoeken en eigendomsinformatie.
          </p>
        </fieldset>

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded px-3 py-2 text-sm">
            Annuleren
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim() || !parcelIds.length || !preview}
            className="rounded bg-bright-yellow px-4 py-2 text-sm font-bold hover:bg-yellow disabled:opacity-50"
          >
            {busy ? "Starten…" : "Start project"}
          </button>
        </div>
      </form>
    </div>
  );
};
